<template>
  <div class="candidate-detail container py-5">
    <div v-if="blocked" class="banner blocked">
      ⚠ Profiliniz bloklanıb. CV-yə baxış mövcud deyil.
    </div>

    <div v-else-if="loading" class="status" v-tx="'DetailLoading'">{{ tx('DetailLoading') }}</div>

    <div v-else-if="!nurse" class="status" v-tx="'DetailNotFound'">{{ tx('DetailNotFound') }}</div>

    <div v-else class="cv-wrap">
      <div class="actions-bar">
        <router-link :to="{ name: 'candidates' }" class="back" v-tx="'DetailBack'">‹ {{ tx('DetailBack') }}</router-link>
        <div class="actions">
          <button type="button" class="ghost" @click="downloadPdf" :disabled="downloading" v-tx="'DetailDownloadPdf'">
            {{ downloading ? tx('DetailPreparing') : tx('DetailDownloadPdf') }}
          </button>
          <button v-if="nurse && nurse.hasApplied" type="button" class="applied" disabled v-tx="'DetailApplied'">✓ {{ tx('DetailApplied') }}</button>
          <button v-else type="button" class="primary" @click="openApplyDialog" v-tx="'DetailApply'">{{ tx('DetailApply') }}</button>
        </div>
      </div>

      <div class="tabs">
        <button type="button" class="tab" :class="{ active: activeTab === 'cv' }" @click="activeTab = 'cv'" v-tx="'CvTabTitle'">{{ tx('CvTabTitle') }}</button>
        <button type="button" class="tab" :class="{ active: activeTab === 'documents' }" @click="activeTab = 'documents'" v-tx="'DocumentsTabTitle'">
          {{ tx('DocumentsTabTitle') }}
          <span v-if="documentsCount" class="badge">{{ documentsCount }}</span>
        </button>
        <button type="button" class="tab" :class="{ active: activeTab === 'exchanges' }" @click="activeTab = 'exchanges'" v-tx="'ExchangesTabTitle'">
          {{ tx('ExchangesTabTitle') }}
          <span v-if="exchanges.length" class="badge">{{ exchanges.length }}</span>
        </button>
      </div>

      <article class="cv" :lang="$store.state.lang || 'az'">
        <header class="cv-header">
          <div class="avatar" :class="{ 'has-photo': nurse.photoUrl }">
            <img v-if="nurse.photoUrl" :src="nurse.photoUrl" alt="" />
            <span v-else>{{ initials(nurse) }}</span>
          </div>
          <div>
            <h1>{{ nurse.name }} {{ nurse.surname }}</h1>
            <p class="muted">
              <span v-if="nurse.birthDate" v-tx="'DetailBirth'">{{ tx('DetailBirth') }}: {{ formatDate(nurse.birthDate) }}</span>
              <span v-if="nurse.birthDate && nurse.citizenship"> • </span>
              <span v-if="nurse.citizenship">{{ nurse.citizenship }}</span>
            </p>
          </div>
        </header>

        <div v-show="activeTab === 'cv'">
          <section v-if="nurse.companyContact" class="section section--contact">
            <h3 v-tx="'CvContactTitle'">{{ tx('CvContactTitle') }}</h3>
            <div class="contact-card">
              <div v-if="nurse.companyContact.companyName" class="contact-row company">
                <span class="value">{{ nurse.companyContact.companyName }}</span>
              </div>
              <div class="contact-grid">
                <div v-if="nurse.companyContact.email" class="contact-row">
                  <span class="label" v-tx="'CvEmailLabel'">{{ tx('CvEmailLabel') }}</span>
                  <a :href="`mailto:${nurse.companyContact.email}`" class="value">{{ nurse.companyContact.email }}</a>
                </div>
                <div v-if="nurse.companyContact.phone" class="contact-row">
                  <span class="label" v-tx="'CvPhoneLabel'">{{ tx('CvPhoneLabel') }}</span>
                  <a :href="`tel:${nurse.companyContact.phone}`" class="value">{{ nurse.companyContact.phone }}</a>
                </div>
                <div v-if="nurse.companyContact.address" class="contact-row full">
                  <span class="label" v-tx="'CvAddressLabel'">{{ tx('CvAddressLabel') }}</span>
                  <span class="value">{{ nurse.companyContact.address }}</span>
                </div>
                <div v-if="nurse.companyContact.website" class="contact-row full">
                  <span class="label" v-tx="'CvWebsiteLabel'">{{ tx('CvWebsiteLabel') }}</span>
                  <a :href="nurse.companyContact.website" target="_blank" rel="noopener" class="value">{{ nurse.companyContact.website }}</a>
                </div>
              </div>
            </div>
          </section>

          <section v-if="nurse.educations && nurse.educations.length" class="section">
            <h3 v-tx="'CvEducationTitle'">{{ tx('CvEducationTitle') }}</h3>
            <div class="section-body">
              <div v-for="e in nurse.educations" :key="e.id" class="item">
                <strong>{{ e.institutionName }}</strong>
                <span v-if="e.professionName" class="muted"> — {{ e.professionName }}</span>
                <div class="muted small">{{ formatDate(e.startDate) }} — {{ formatDate(e.endDate) }}</div>
              </div>
            </div>
          </section>

          <section v-if="nurse.workExperiences && nurse.workExperiences.length" class="section">
            <h3 v-tx="'CvExperienceTitle'">{{ tx('CvExperienceTitle') }}</h3>
            <div class="section-body">
              <div v-for="w in nurse.workExperiences" :key="w.id" class="item">
                <strong>{{ w.position }}</strong> <span class="muted">— {{ w.workplace }}</span>
                <div class="muted small">{{ formatDate(w.startDate) }} — {{ w.endDate ? formatDate(w.endDate) : tx('CvPresent') }}</div>
                <p v-if="w.duties" class="duties">{{ w.duties }}</p>
              </div>
            </div>
          </section>

          <section v-if="nurse.languages && nurse.languages.length" class="section">
            <h3 v-tx="'CvLanguagesTitle'">{{ tx('CvLanguagesTitle') }}</h3>
            <div class="section-body">
              <div v-for="l in nurse.languages" :key="l.id" class="item">
                <strong>{{ l.languageName }}</strong> <span class="muted">— {{ l.level }}</span>
              </div>
            </div>
          </section>

          <section v-if="nurse.certificates && nurse.certificates.length" class="section">
            <h3 v-tx="'CvCertificatesTitle'">{{ tx('CvCertificatesTitle') }}</h3>
            <div class="section-body">
              <div v-for="c in nurse.certificates" :key="c.id" class="item">
                <strong>{{ c.title }}</strong>
                <span v-if="c.issuedBy" class="muted"> — {{ c.issuedBy }}</span>
                <div v-if="c.issuedDate" class="muted small">{{ formatDate(c.issuedDate) }}</div>
              </div>
            </div>
          </section>

          <section v-if="nurse.skills && nurse.skills.length" class="section">
            <h3 v-tx="'CvSkillsTitle'">{{ tx('CvSkillsTitle') }}</h3>
            <div class="section-body">
              <ul>
                <li v-for="s in nurse.skills" :key="s.id">{{ s.description }}</li>
              </ul>
            </div>
          </section>
        </div>

        <div v-show="activeTab === 'documents'">
          <section class="section documents">
            <h3 v-tx="'DocumentsSectionTitle'">{{ tx('DocumentsSectionTitle') }}</h3>
            <p v-if="!documentsCount" class="empty" v-tx="'DocumentsEmpty'">{{ tx('DocumentsEmpty') }}</p>
            <div v-else class="doc-list">
              <a
                v-for="d in nurse.documents"
                :key="d.id"
                :href="d.fileUrl"
                target="_blank"
                rel="noopener"
                class="doc-row"
              >
                <svg class="doc-icon" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#2a5298" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span class="doc-meta">
                  <strong>{{ d.documentTypeName || tx('DocumentItemFallback') }}</strong>
                </span>
                <span class="doc-action" v-tx="'DocumentDownload'">{{ tx('DocumentDownload') }}</span>
              </a>
            </div>
          </section>
        </div>

        <div v-show="activeTab === 'exchanges'">
          <section class="section exchanges">
            <h3 v-tx="'ExchangesSectionTitle'">{{ tx('ExchangesSectionTitle') }}</h3>

            <form class="upload-form" @submit.prevent="uploadExchange">
              <label class="field">
                <span v-tx="'ExchangesNameLabel'">{{ tx('ExchangesNameLabel') }} *</span>
                <input type="text" v-model="newExchangeTitle" required maxlength="300" :placeholder="tx('ExchangesNamePlaceholder')" />
              </label>
              <label class="field">
                <span v-tx="'ExchangesNoteLabel'">{{ tx('ExchangesNoteLabel') }}</span>
                <textarea v-model="newExchangeNote" maxlength="2000" rows="2"></textarea>
              </label>
              <label class="field">
                <span v-tx="'ExchangesFileLabel'">{{ tx('ExchangesFileLabel') }} *</span>
                <div class="file-picker">
                  <button type="button" class="file-btn" @click="triggerExchangeFile" v-tx="'ChooseFile'">{{ tx('ChooseFile') }}</button>
                  <span class="file-name" :class="{ empty: !newExchangeFile }" v-tx="'NoFileChosen'">{{ newExchangeFile ? newExchangeFile.name : tx('NoFileChosen') }}</span>
                  <input ref="exchangeFileInput" type="file" class="hidden-file" @change="onExchangeFileChange" />
                </div>
              </label>
              <button type="submit" class="primary" :disabled="exchangeUploading || !newExchangeFile || !newExchangeTitle.trim()" v-tx="'ExchangesSubmit'">
                {{ exchangeUploading ? tx('ExchangesUploading') : tx('ExchangesSubmit') }}
              </button>
            </form>

            <h4 class="docs-title" v-tx="'ExchangesListTitle'">{{ tx('ExchangesListTitle') }}</h4>
            <p v-if="!exchanges.length" class="empty" v-tx="'ExchangesEmpty'">{{ tx('ExchangesEmpty') }}</p>
            <ul v-else class="exchange-list">
              <li v-for="ex in exchanges" :key="ex.id" class="exchange-row">
                <div class="ex-head">
                  <strong>{{ ex.title }}</strong>
                  <span class="status-badge" :class="ex.status === 'Responded' ? 'responded' : 'pending'" v-tx="'ExchangesStatusPending'">
                    {{ ex.status === 'Responded' ? tx('ExchangesStatusResponded') : tx('ExchangesStatusPending') }}
                  </span>
                </div>
                <div class="ex-body">
                  <div class="ex-side">
                    <span class="label" v-tx="'ExchangesYourUpload'">{{ tx('ExchangesYourUpload') }}</span>
                    <a :href="ex.clinicFileUrl" target="_blank" rel="noopener">{{ ex.clinicFileName }}</a>
                    <p v-if="ex.clinicNote" class="note">{{ ex.clinicNote }}</p>
                    <span class="when">{{ formatDate(ex.clinicUploadedDate) }}</span>
                  </div>
                  <div class="ex-side">
                    <span class="label" v-tx="'ExchangesReplyLabel'">{{ tx('ExchangesReplyLabel') }}</span>
                    <template v-if="ex.adminFileUrl">
                      <a :href="ex.adminFileUrl" target="_blank" rel="noopener">{{ ex.adminFileName }}</a>
                      <p v-if="ex.adminNote" class="note">{{ ex.adminNote }}</p>
                      <span class="when">{{ formatDate(ex.adminUploadedDate) }}</span>
                    </template>
                    <span v-else class="muted small" v-tx="'ExchangesNoReply'">{{ tx('ExchangesNoReply') }}</span>
                  </div>
                </div>
                <div class="ex-actions">
                  <button type="button" class="link-danger" :disabled="exchangeDeletingId === ex.id" @click="deleteExchange(ex.id)" v-tx="'ExchangesDelete'">
                    {{ exchangeDeletingId === ex.id ? '…' : tx('ExchangesDelete') }}
                  </button>
                </div>
              </li>
            </ul>
          </section>
        </div>
      </article>
    </div>

    <!-- Apply modal -->
    <div v-if="applyOpen" class="apply-backdrop" @click="closeApply">
      <div class="apply-modal" @click.stop>
        <header>
          <h3 v-tx="'ApplyTitle'">{{ tx('ApplyTitle') }}</h3>
          <button type="button" class="close" @click="closeApply">✕</button>
        </header>
        <div class="body">
          <p class="muted" v-tx="'ApplyHint'">{{ tx('ApplyHint') }}</p>
          <label class="field">
            <span v-tx="'ApplyMsgLabel'">{{ tx('ApplyMsgLabel') }}</span>
            <textarea v-model="applyMessage" rows="5" maxlength="2000" :placeholder="tx('ApplyMsgPlaceholder')"></textarea>
          </label>
        </div>
        <footer>
          <button type="button" class="ghost" @click="closeApply" :disabled="applying" v-tx="'ApplyCancel'">{{ tx('ApplyCancel') }}</button>
          <button type="button" class="primary" @click="submitApply" :disabled="applying" v-tx="'ApplySend'">
            {{ applying ? tx('ApplySending') : tx('ApplySend') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const CV_FALLBACK = {
  az: {
    CvTabTitle: "CV",
    DocumentsTabTitle: "Sənədlər",
    ExchangesTabTitle: "İşçi sənədləri",
    CvContactTitle: "Əlaqə",
    CvCompanyLabel: "Şirkət",
    CvEmailLabel: "Email",
    CvPhoneLabel: "Telefon",
    CvAddressLabel: "Ünvan",
    CvWebsiteLabel: "Vebsayt",
    CvEducationTitle: "Təhsil",
    CvExperienceTitle: "İş təcrübəsi",
    CvPresent: "hal-hazırda",
    ChooseFile: "Fayl seçin",
    NoFileChosen: "Fayl seçilməyib",
    CvLanguagesTitle: "Dil bilikləri",
    CvCertificatesTitle: "Sertifikatlar",
    CvSkillsTitle: "Bacarıqlar",
    DocumentsSectionTitle: "Təsdiqlənmiş sənədlər",
    DocumentsEmpty: "Hələ təsdiqlənmiş sənəd yoxdur.",
    DocumentItemFallback: "Sənəd",
    DocumentDownload: "Yüklə",
    ExchangesSectionTitle: "İşçi sənədləri",
    ExchangesNameLabel: "Sənədin adı",
    ExchangesNamePlaceholder: "Məs. Əmək müqaviləsi",
    ExchangesNoteLabel: "Qeyd (istəyə bağlı)",
    ExchangesFileLabel: "Fayl",
    ExchangesSubmit: "Göndər",
    ExchangesUploading: "Yüklənir…",
    ExchangesListTitle: "Mənim sənəd mübadiləm",
    ExchangesEmpty: "Hələ heç bir sənəd göndərməmisiniz.",
    ExchangesStatusResponded: "Cavablandı",
    ExchangesStatusPending: "Gözləyir",
    ExchangesYourUpload: "Sizin yüklədiyiniz",
    ExchangesReplyLabel: "Cavab",
    ExchangesNoReply: "Hələ cavab yoxdur",
    ExchangesDelete: "Sil",
    DetailLoading: "Yüklənir…", DetailNotFound: "CV tapılmadı.", DetailBack: "Siyahıya qayıt",
    DetailPreparing: "Hazırlanır…", DetailDownloadPdf: "PDF yüklə", DetailApplied: "Müraciət edilib",
    DetailApply: "Müraciət et", DetailBirth: "Doğum",
    ApplyTitle: "Müraciət göndər", ApplyHint: "Mesajınız namizədə email vasitəsilə çatdırılacaq.",
    ApplyMsgLabel: "Mesaj (istəyə bağlı)", ApplyMsgPlaceholder: "Salamlama, klinikanız haqqında məlumat, vakansiya...",
    ApplyCancel: "Ləğv et", ApplySending: "Göndərilir…", ApplySend: "Göndər", ApplySuccess: "Müraciət göndərildi",
  },
  de: {
    CvTabTitle: "Lebenslauf",
    DocumentsTabTitle: "Dokumente",
    ExchangesTabTitle: "Mitarbeiter-Dokumente",
    CvContactTitle: "Kontakt",
    CvCompanyLabel: "Firma",
    CvEmailLabel: "E-Mail",
    CvPhoneLabel: "Telefon",
    CvAddressLabel: "Adresse",
    CvWebsiteLabel: "Webseite",
    CvEducationTitle: "Ausbildung",
    CvExperienceTitle: "Berufserfahrung",
    CvPresent: "bis heute",
    ChooseFile: "Datei auswählen",
    NoFileChosen: "Keine Datei ausgewählt",
    CvLanguagesTitle: "Sprachkenntnisse",
    CvCertificatesTitle: "Zertifikate",
    CvSkillsTitle: "Fähigkeiten",
    DocumentsSectionTitle: "Bestätigte Dokumente",
    DocumentsEmpty: "Noch keine bestätigten Dokumente.",
    DocumentItemFallback: "Dokument",
    DocumentDownload: "Herunterladen",
    ExchangesSectionTitle: "Mitarbeiter-Dokumente",
    ExchangesNameLabel: "Name des Dokuments",
    ExchangesNamePlaceholder: "z. B. Arbeitsvertrag",
    ExchangesNoteLabel: "Notiz (optional)",
    ExchangesFileLabel: "Datei",
    ExchangesSubmit: "Senden",
    ExchangesUploading: "Wird hochgeladen…",
    ExchangesListTitle: "Mein Dokumentenaustausch",
    ExchangesEmpty: "Sie haben noch keine Dokumente gesendet.",
    ExchangesStatusResponded: "Beantwortet",
    ExchangesStatusPending: "Ausstehend",
    ExchangesYourUpload: "Ihr Upload",
    ExchangesReplyLabel: "Antwort",
    ExchangesNoReply: "Noch keine Antwort",
    ExchangesDelete: "Löschen",
    DetailLoading: "Wird geladen…", DetailNotFound: "Lebenslauf nicht gefunden.", DetailBack: "Zur Liste",
    DetailPreparing: "Wird vorbereitet…", DetailDownloadPdf: "PDF herunterladen", DetailApplied: "Beworben",
    DetailApply: "Bewerben", DetailBirth: "Geburtsdatum",
    ApplyTitle: "Bewerbung senden", ApplyHint: "Ihre Nachricht wird dem Kandidaten per E-Mail zugestellt.",
    ApplyMsgLabel: "Nachricht (optional)", ApplyMsgPlaceholder: "Begrüßung, Infos über Ihre Klinik, Stelle...",
    ApplyCancel: "Abbrechen", ApplySending: "Wird gesendet…", ApplySend: "Senden", ApplySuccess: "Bewerbung gesendet",
  },
  en: {
    CvTabTitle: "CV",
    DocumentsTabTitle: "Documents",
    ExchangesTabTitle: "Employee documents",
    CvContactTitle: "Contact",
    CvCompanyLabel: "Company",
    CvEmailLabel: "Email",
    CvPhoneLabel: "Phone",
    CvAddressLabel: "Address",
    CvWebsiteLabel: "Website",
    CvEducationTitle: "Education",
    CvExperienceTitle: "Work experience",
    CvPresent: "present",
    ChooseFile: "Choose file",
    NoFileChosen: "No file chosen",
    CvLanguagesTitle: "Languages",
    CvCertificatesTitle: "Certificates",
    CvSkillsTitle: "Skills",
    DocumentsSectionTitle: "Approved documents",
    DocumentsEmpty: "No approved documents yet.",
    DocumentItemFallback: "Document",
    DocumentDownload: "Download",
    ExchangesSectionTitle: "Employee documents",
    ExchangesNameLabel: "Document name",
    ExchangesNamePlaceholder: "e.g. Employment contract",
    ExchangesNoteLabel: "Note (optional)",
    ExchangesFileLabel: "File",
    ExchangesSubmit: "Send",
    ExchangesUploading: "Uploading…",
    ExchangesListTitle: "My document exchanges",
    ExchangesEmpty: "You haven't sent any documents yet.",
    ExchangesStatusResponded: "Responded",
    ExchangesStatusPending: "Pending",
    ExchangesYourUpload: "Your upload",
    ExchangesReplyLabel: "Reply",
    ExchangesNoReply: "No reply yet",
    ExchangesDelete: "Delete",
    DetailLoading: "Loading…", DetailNotFound: "CV not found.", DetailBack: "Back to list",
    DetailPreparing: "Preparing…", DetailDownloadPdf: "Download PDF", DetailApplied: "Applied",
    DetailApply: "Apply", DetailBirth: "Born",
    ApplyTitle: "Send application", ApplyHint: "Your message will be delivered to the candidate by email.",
    ApplyMsgLabel: "Message (optional)", ApplyMsgPlaceholder: "Greeting, info about your clinic, position...",
    ApplyCancel: "Cancel", ApplySending: "Sending…", ApplySend: "Send", ApplySuccess: "Application sent",
  },
  ru: {
    CvTabTitle: "CV",
    DocumentsTabTitle: "Документы",
    ExchangesTabTitle: "Документы сотрудника",
    CvContactTitle: "Контакт",
    CvCompanyLabel: "Компания",
    CvEmailLabel: "Email",
    CvPhoneLabel: "Телефон",
    CvAddressLabel: "Адрес",
    CvWebsiteLabel: "Вебсайт",
    CvEducationTitle: "Образование",
    CvExperienceTitle: "Опыт работы",
    CvPresent: "по наст. время",
    ChooseFile: "Выберите файл",
    NoFileChosen: "Файл не выбран",
    CvLanguagesTitle: "Языки",
    CvCertificatesTitle: "Сертификаты",
    CvSkillsTitle: "Навыки",
    DocumentsSectionTitle: "Подтверждённые документы",
    DocumentsEmpty: "Пока нет подтверждённых документов.",
    DocumentItemFallback: "Документ",
    DocumentDownload: "Скачать",
    ExchangesSectionTitle: "Документы сотрудника",
    ExchangesNameLabel: "Название документа",
    ExchangesNamePlaceholder: "Напр. Трудовой договор",
    ExchangesNoteLabel: "Примечание (необязательно)",
    ExchangesFileLabel: "Файл",
    ExchangesSubmit: "Отправить",
    ExchangesUploading: "Загрузка…",
    ExchangesListTitle: "Мои обмены документами",
    ExchangesEmpty: "Вы ещё не отправили ни одного документа.",
    ExchangesStatusResponded: "Отвечено",
    ExchangesStatusPending: "Ожидает",
    ExchangesYourUpload: "Ваша загрузка",
    ExchangesReplyLabel: "Ответ",
    ExchangesNoReply: "Ответа пока нет",
    ExchangesDelete: "Удалить",
    DetailLoading: "Загрузка…", DetailNotFound: "CV не найдено.", DetailBack: "К списку",
    DetailPreparing: "Подготовка…", DetailDownloadPdf: "Скачать PDF", DetailApplied: "Заявка отправлена",
    DetailApply: "Откликнуться", DetailBirth: "Дата рождения",
    ApplyTitle: "Отправить заявку", ApplyHint: "Ваше сообщение будет доставлено кандидату по email.",
    ApplyMsgLabel: "Сообщение (необязательно)", ApplyMsgPlaceholder: "Приветствие, информация о клинике, вакансия...",
    ApplyCancel: "Отмена", ApplySending: "Отправка…", ApplySend: "Отправить", ApplySuccess: "Заявка отправлена",
  },
  tr: {
    CvTabTitle: "CV",
    DocumentsTabTitle: "Belgeler",
    ExchangesTabTitle: "Çalışan belgeleri",
    CvContactTitle: "İletişim",
    CvCompanyLabel: "Şirket",
    CvEmailLabel: "E-posta",
    CvPhoneLabel: "Telefon",
    CvAddressLabel: "Adres",
    CvWebsiteLabel: "Web sitesi",
    CvEducationTitle: "Eğitim",
    CvExperienceTitle: "İş deneyimi",
    CvPresent: "hâlen",
    ChooseFile: "Dosya seçin",
    NoFileChosen: "Dosya seçilmedi",
    CvLanguagesTitle: "Diller",
    CvCertificatesTitle: "Sertifikalar",
    CvSkillsTitle: "Beceriler",
    DocumentsSectionTitle: "Onaylanmış belgeler",
    DocumentsEmpty: "Henüz onaylanmış belge yok.",
    DocumentItemFallback: "Belge",
    DocumentDownload: "İndir",
    ExchangesSectionTitle: "Çalışan belgeleri",
    ExchangesNameLabel: "Belge adı",
    ExchangesNamePlaceholder: "Örn. İş sözleşmesi",
    ExchangesNoteLabel: "Not (isteğe bağlı)",
    ExchangesFileLabel: "Dosya",
    ExchangesSubmit: "Gönder",
    ExchangesUploading: "Yükleniyor…",
    ExchangesListTitle: "Belge alışverişlerim",
    ExchangesEmpty: "Henüz hiçbir belge göndermediniz.",
    ExchangesStatusResponded: "Yanıtlandı",
    ExchangesStatusPending: "Beklemede",
    ExchangesYourUpload: "Yüklediğiniz",
    ExchangesReplyLabel: "Yanıt",
    ExchangesNoReply: "Henüz yanıt yok",
    ExchangesDelete: "Sil",
    DetailLoading: "Yükleniyor…", DetailNotFound: "CV bulunamadı.", DetailBack: "Listeye dön",
    DetailPreparing: "Hazırlanıyor…", DetailDownloadPdf: "PDF indir", DetailApplied: "Başvuruldu",
    DetailApply: "Başvur", DetailBirth: "Doğum",
    ApplyTitle: "Başvuru gönder", ApplyHint: "Mesajınız adaya e-posta ile iletilecek.",
    ApplyMsgLabel: "Mesaj (isteğe bağlı)", ApplyMsgPlaceholder: "Selamlama, kliniğiniz hakkında bilgi, pozisyon...",
    ApplyCancel: "İptal", ApplySending: "Gönderiliyor…", ApplySend: "Gönder", ApplySuccess: "Başvuru gönderildi",
  },
};

export default {
  name: "CandidateDetailPage",
  data() {
    return {
      nurse: null,
      loading: true,
      blocked: false,
      downloading: false,
      applyOpen: false,
      applyMessage: "",
      applying: false,
      activeTab: "cv",
      exchanges: [],
      newExchangeTitle: "",
      newExchangeNote: "",
      newExchangeFile: null,
      exchangeUploading: false,
      exchangeDeletingId: null,
    };
  },
  computed: {
    id() { return Number(this.$route.params.id); },
    documentsCount() { return (this.nurse && this.nurse.documents && this.nurse.documents.length) || 0; },
  },
  watch: {
    id() { this.load(); this.loadExchanges(); }
  },
  mounted() { this.load(); this.loadExchanges(); },
  methods: {
    async load() {
      this.loading = true;
      this.nurse = null;
      try {
        const res = await axios.get(`/clinic/nurses/${this.id}`);
        this.nurse = res.data?.data || null;
      } catch (err) {
        if (err?.response?.status === 403) this.blocked = true;
      } finally {
        this.loading = false;
      }
    },
    async downloadPdf() {
      this.downloading = true;
      try {
        const res = await axios.get(`/clinic/nurses/${this.id}/cv.pdf`, { responseType: 'blob' });
        const blob = res.data;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `cv-${this.nurse.name}-${this.nurse.surname}.pdf`.replace(/\s+/g, '-');
        a.click();
        URL.revokeObjectURL(a.href);
      } finally {
        this.downloading = false;
      }
    },
    openApplyDialog() { this.applyOpen = true; this.applyMessage = ""; },
    closeApply() { if (this.applying) return; this.applyOpen = false; },
    async submitApply() {
      this.applying = true;
      try {
        await axios.post(`/clinic/nurses/${this.id}/apply`, { message: this.applyMessage || null });
        this.$toast.success(this.tx('ApplySuccess'));
        this.applyOpen = false;
        if (this.nurse) this.nurse = { ...this.nurse, hasApplied: true };
      } catch (e) {
        // toast
      } finally {
        this.applying = false;
      }
    },
    initials(n) {
      const a = (n?.name || "").charAt(0).toUpperCase();
      const b = (n?.surname || "").charAt(0).toUpperCase();
      return (a + b) || "?";
    },
    formatDate(d) {
      if (!d) return '—';
      const s = String(d);
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[3]}.${m[2]}.${m[1]}`;
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return s;
      const dd = String(dt.getDate()).padStart(2, '0');
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      return `${dd}.${mm}.${dt.getFullYear()}`;
    },
    tx(key) {
      const v = this.$store.state.statics?.[key];
      if (v && String(v).trim()) return v;
      const lang = this.$store.state.lang || "az";
      return (CV_FALLBACK[lang] || CV_FALLBACK.az)[key] || "";
    },
    async loadExchanges() {
      try {
        const res = await axios.get(`/clinic/nurse-exchanges`, { params: { nurseId: this.id } });
        this.exchanges = res.data?.data || [];
      } catch {
        this.exchanges = [];
      }
    },
    triggerExchangeFile() {
      if (this.$refs.exchangeFileInput) this.$refs.exchangeFileInput.click();
    },
    onExchangeFileChange(ev) {
      this.newExchangeFile = (ev.target.files && ev.target.files[0]) || null;
    },
    async uploadExchange() {
      if (!this.newExchangeFile || !this.newExchangeTitle.trim()) return;
      this.exchangeUploading = true;
      try {
        const fd = new FormData();
        fd.append("NurseProfileId", String(this.id));
        fd.append("Title", this.newExchangeTitle.trim());
        if (this.newExchangeNote.trim()) fd.append("Note", this.newExchangeNote.trim());
        fd.append("File", this.newExchangeFile);
        await axios.post(`/clinic/nurse-exchanges`, fd);
        this.$toast.success("Göndərildi");
        this.newExchangeTitle = "";
        this.newExchangeNote = "";
        this.newExchangeFile = null;
        if (this.$refs.exchangeFileInput) this.$refs.exchangeFileInput.value = "";
        await this.loadExchanges();
      } catch (e) {
        // toast
      } finally {
        this.exchangeUploading = false;
      }
    },
    async deleteExchange(id) {
      this.exchangeDeletingId = id;
      try {
        await axios.delete(`/clinic/nurse-exchanges/${id}`);
        this.$toast.success("Silindi");
        await this.loadExchanges();
      } catch (e) {
        // toast
      } finally {
        this.exchangeDeletingId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.candidate-detail { min-height: 60vh; max-width: 900px; }
.banner.blocked { background: #fdecea; color: #c0392b; padding: 1rem; border-radius: 8px; border: 1px solid #f1c2bd; }
.status { background: #fff; padding: 1.5rem; border-radius: 8px; text-align: center; color: #666; }

.actions-bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; flex-wrap: wrap; gap: 0.5rem;
  .back { color: #2a5298; text-decoration: none; font-size: 0.9rem; &:hover { text-decoration: underline; } }
  .actions { display: flex; gap: 0.5rem;
    button { padding: 0.55rem 1.1rem; border-radius: 6px; cursor: pointer; font: inherit; border: 1px solid transparent; font-size: 0.9rem;
      &.primary { background: #2a5298; color: #fff; border-color: #2a5298; &:hover:not(:disabled) { background: #1e3c72; } }
      &.ghost { background: #fff; color: #475569; border-color: #d0d5dd; &:hover:not(:disabled) { background: #f7f9fc; } }
      &.applied { background: #e6f6ed; color: #1e7d44; border-color: #b6e5c8; cursor: default; }
      &:disabled:not(.applied) { opacity: 0.6; cursor: not-allowed; }
    }
  }
}

.cv { background: #fff; border-radius: 0 0 12px 12px; padding: 1.25rem 1.5rem 1.5rem; box-shadow: 0 2px 14px rgba(0,0,0,0.06); }
.cv-header { display: flex; gap: 0.85rem; align-items: center; padding-bottom: 0.75rem; border-bottom: 2px solid #1e3c72; margin-bottom: 1rem;
  .avatar { width: 58px; height: 58px; border-radius: 50%; background: #e6f0ff; color: #2a5298; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; font-weight: 700; flex-shrink: 0; overflow: hidden;
    &.has-photo { width: 70px; height: 86px; border-radius: 8px; }
    img { width: 100%; height: 100%; object-fit: cover; display: block; }
  }
  h1 { color: #1e3c72; margin: 0; font-size: 1.2rem; line-height: 1.2; }
  .muted { color: #64748b; margin: 0.15rem 0 0; font-size: 0.82rem; }
}

.tabs { display: flex; gap: 0.25rem; border-bottom: 1px solid #e2e8f0; margin-bottom: 0; background: #fff; border-radius: 12px 12px 0 0; padding: 0 0.5rem;
  .tab { background: transparent; border: 0; padding: 0.7rem 1rem; cursor: pointer; font: inherit; font-size: 0.9rem; color: #64748b; border-bottom: 2px solid transparent; margin-bottom: -1px; display: inline-flex; align-items: center; gap: 0.4rem;
    &:hover { color: #1e3c72; }
    &.active { color: #1e3c72; border-bottom-color: #2a5298; font-weight: 600; }
    .badge { background: #e6f0ff; color: #2a5298; font-size: 0.7rem; padding: 0.1rem 0.45rem; border-radius: 999px; font-weight: 600; }
    &.active .badge { background: #2a5298; color: #fff; }
  }
}

.exchanges {
  .empty { color: #64748b; font-size: 0.88rem; margin: 0 0 1rem; }
  .upload-form { background: #f7faff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 1rem; margin-bottom: 1.5rem;
    .field { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 0.7rem;
      span { font-size: 0.78rem; color: #475569; font-weight: 600; }
      input, textarea { padding: 0.55rem 0.75rem; border: 1px solid #d0d5dd; border-radius: 6px; font: inherit; outline: none; resize: vertical;
        &:focus { border-color: #2a5298; }
      }
    }
    .file-picker { display: flex; align-items: center; gap: 0.65rem;
      .hidden-file { display: none; }
      .file-btn { flex-shrink: 0; background: #fff; color: #2a5298; border: 1px solid #2a5298; border-radius: 6px; padding: 0.5rem 0.95rem; font: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer;
        &:hover { background: #eef3fb; }
      }
      .file-name { font-size: 0.85rem; color: #475569; word-break: break-all;
        &.empty { color: #94a3b8; }
      }
    }
    button.primary { background: #2a5298; color: #fff; border: 0; border-radius: 6px; padding: 0.55rem 1.25rem; font: inherit; font-weight: 600; cursor: pointer;
      &:hover:not(:disabled) { background: #1e3c72; }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }
  }
  .docs-title { color: #1e3c72; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; margin: 0 0 0.6rem; }
  .exchange-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.75rem;
    .exchange-row { border: 1px solid #e2e8f0; border-radius: 8px; padding: 0.85rem 1rem; background: #fff;
      .ex-head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-bottom: 0.6rem;
        strong { color: #1e3c72; font-size: 0.95rem; }
        .status-badge { font-size: 0.72rem; padding: 0.15rem 0.55rem; border-radius: 999px; font-weight: 600;
          &.pending { background: #fef3c7; color: #92400e; }
          &.responded { background: #d1fae5; color: #047857; }
        }
      }
      .ex-body { display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem;
        @media (max-width: 575px) { grid-template-columns: 1fr; }
        .ex-side { display: flex; flex-direction: column; gap: 0.2rem; background: #f8fafc; border-radius: 6px; padding: 0.6rem 0.75rem;
          .label { font-size: 0.7rem; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
          a { color: #2a5298; font-size: 0.85rem; word-break: break-all; text-decoration: none;
            &:hover { text-decoration: underline; }
          }
          .note { color: #475569; font-size: 0.85rem; margin: 0.25rem 0 0; white-space: pre-wrap; }
          .when { color: #94a3b8; font-size: 0.75rem; margin-top: 0.2rem; }
          .muted { color: #64748b; }
          .small { font-size: 0.82rem; }
        }
      }
      .ex-actions { display: flex; justify-content: flex-end; margin-top: 0.5rem;
        .link-danger { background: transparent; border: 0; color: #c0392b; cursor: pointer; font: inherit; font-size: 0.83rem; padding: 0.25rem 0.5rem;
          &:hover:not(:disabled) { text-decoration: underline; }
          &:disabled { opacity: 0.6; cursor: not-allowed; }
        }
      }
    }
  }
}

.documents { .empty { color: #64748b; font-size: 0.9rem; margin: 0; }
  .doc-list { display: flex; flex-direction: column; gap: 0.5rem; }
  .doc-row { display: flex; align-items: center; gap: 0.85rem; padding: 0.75rem 1rem; border: 1px solid #e2e8f0; border-radius: 8px; text-decoration: none; color: inherit; transition: border-color 0.15s, background 0.15s;
    &:hover { border-color: #2a5298; background: #f7faff; .doc-action { background: #2a5298; color: #fff; } }
    .doc-icon { flex-shrink: 0; }
    .doc-meta { display: flex; flex-direction: column; gap: 0.15rem; flex: 1; min-width: 0;
      strong { color: #1e3c72; font-size: 0.95rem; }
      .muted { color: #64748b; }
      .small { font-size: 0.8rem; word-break: break-all; }
    }
    .doc-action { background: #fff; color: #2a5298; border: 1px solid #2a5298; padding: 0.35rem 0.85rem; border-radius: 6px; font-size: 0.82rem; font-weight: 600; white-space: nowrap; }
  }
}

/* CV sections — hər biri eyni kart şəklində */
.cv .section {
  display: block !important;
  box-sizing: border-box !important;
  width: 100% !important;
  margin: 0 0 3rem !important;
  padding: 0 !important;

  &:last-child { margin-bottom: 0 !important; }

  > h3 {
    color: #1e3c72;
    font-size: 0.72rem;
    margin: 0 0 0.3rem;
    padding: 0 0.15rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    border: 0;
  }

  > .section-body,
  > .contact-card {
    display: block;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    background: #f7faff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.6rem 0.85rem;
    overflow: hidden;
    margin: 0;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
  }

  .item {
    padding: 0.4rem 0;
    border-bottom: 1px dashed #d8e3f5;
    &:first-child { padding-top: 0; }
    &:last-child { border-bottom: 0; padding-bottom: 0; }
    strong { color: #1e3c72; font-size: 0.92rem; }
    .muted { color: #64748b; }
    .small { font-size: 0.76rem; margin-top: 0.1rem; }
    .duties { margin: 0.3rem 0 0; color: #475569; font-size: 0.86rem; white-space: pre-wrap; line-height: 1.45; }
  }

  ul {
    padding-left: 1.1rem;
    margin: 0;
    li { margin-bottom: 0.15rem; color: #1e3c72; font-size: 0.9rem;
      &:last-child { margin-bottom: 0; }
    }
  }
}

.cv .section--contact .contact-card {
  .contact-row.company {
    padding-bottom: 0.4rem;
    margin-bottom: 0.45rem;
    border-bottom: 1px solid #d8e3f5;
    .value { color: #1e3c72; font-size: 0.98rem; font-weight: 700; }
  }
  .contact-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.35rem 1.25rem;
    min-width: 0;
    @media (max-width: 575px) { grid-template-columns: 1fr; }
  }
  .contact-row {
    display: flex;
    flex-direction: column;
    gap: 0.05rem;
    min-width: 0;
    overflow: hidden;
    &.full { grid-column: 1 / -1; }
    .label { font-size: 0.66rem; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; }
    .value { color: #1e3c72; word-break: break-word; font-size: 0.88rem; text-decoration: none; line-height: 1.3; min-width: 0;
      &[href]:hover { color: #2a5298; text-decoration: underline; }
    }
  }
}

.apply-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 1rem; }
.apply-modal { background: #fff; border-radius: 12px; width: 100%; max-width: 500px; box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  header { padding: 1rem 1.25rem; border-bottom: 1px solid #eef0f4; display: flex; align-items: center; justify-content: space-between;
    h3 { margin: 0; color: #1e3c72; font-size: 1rem; }
    .close { background: transparent; border: 0; font-size: 1.25rem; cursor: pointer; color: #64748b; padding: 0.25rem 0.5rem; &:hover { color: #1e3c72; } }
  }
  .body { padding: 1.25rem;
    .muted { color: #64748b; font-size: 0.85rem; margin-bottom: 1rem; }
    .field { display: flex; flex-direction: column; gap: 0.3rem;
      span { font-size: 0.78rem; color: #475569; font-weight: 600; }
      textarea { padding: 0.6rem 0.8rem; border: 1px solid #d0d5dd; border-radius: 6px; font: inherit; outline: none; resize: vertical; &:focus { border-color: #2a5298; } }
    }
  }
  footer { padding: 0.85rem 1.25rem; border-top: 1px solid #eef0f4; display: flex; justify-content: flex-end; gap: 0.5rem;
    button { padding: 0.55rem 1.1rem; border-radius: 6px; cursor: pointer; font: inherit; border: 1px solid transparent; font-size: 0.9rem;
      &.primary { background: #2a5298; color: #fff; border-color: #2a5298; &:hover:not(:disabled) { background: #1e3c72; } }
      &.ghost { background: #fff; color: #475569; border-color: #d0d5dd; &:hover:not(:disabled) { background: #f7f9fc; } }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }
  }
}
</style>
